import React, { useEffect, useState } from 'react';
import {
  Row, Col, Card, Button, Modal, Form, Input, Select,
  Space, Tag, Typography, message, Popconfirm, Switch, Empty, Spin,
  Tabs, InputNumber, Upload,
} from 'antd';
import type { UploadProps } from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined,
  FilePdfOutlined, GlobalOutlined, FileTextOutlined, FilterOutlined,
  InboxOutlined, EyeOutlined, ReloadOutlined,
} from '@ant-design/icons';
import { govtApi } from '../services/api';

const { Dragger } = Upload;

// content_url for an uploaded PDF is stored as a private S3 object key, not a
// browsable link — this tells an uploaded file apart from an admin-pasted URL.
const isUploadedKey = (url?: string | null) => !!url && !/^https?:\/\//i.test(url);

const { Title, Text } = Typography;
const { TextArea } = Input;

const SECTIONS = [
  { key: 'General Knowledge',  label: 'General Knowledge',  color: '#1D4ED8', icon: '🌍' },
  { key: 'Polity',             label: 'Polity',             color: '#7C3AED', icon: '⚖️' },
  { key: 'Karnataka History',  label: 'Karnataka History',  color: '#B45309', icon: '🏰' },
  { key: 'Reasoning',          label: 'Reasoning',          color: '#DC2626', icon: '🧠' },
  { key: 'Kannada',            label: 'Kannada',            color: '#7C3AED', icon: '🔤' },
  { key: 'Mathematics',        label: 'Mathematics',        color: '#0891B2', icon: '🔢' },
  { key: 'English',            label: 'English',            color: '#1D4ED8', icon: '📝' },
];

const CONTENT_TYPES = [
  { value: 'typed', label: 'Typed Notes (Markdown)', icon: <FileTextOutlined /> },
  { value: 'pdf',   label: 'PDF Upload',              icon: <FilePdfOutlined /> },
  { value: 'url',   label: 'Website / URL',          icon: <GlobalOutlined /> },
];

const COLORS = [
  { label: 'Blue',    value: '#1D4ED8' },
  { label: 'Cyan',    value: '#0891B2' },
  { label: 'Purple',  value: '#7C3AED' },
  { label: 'Emerald', value: '#059669' },
  { label: 'Amber',   value: '#D97706' },
  { label: 'Rose',    value: '#E11D48' },
  { label: 'Indigo',  value: '#4338CA' },
  { label: 'Teal',    value: '#0F766E' },
];

const StudyMaterialsPage: React.FC = () => {
  const [materials, setMaterials] = useState<any[]>([]);
  const [exams, setExams]         = useState<any[]>([]);
  const [loading, setLoading]     = useState(false);
  const [activeSection, setActiveSection] = useState('General Knowledge');
  const [filterExam, setFilterExam]       = useState('');

  const [modal, setModal]             = useState(false);
  const [editing, setEditing]         = useState<any>(null);
  const [form]                        = Form.useForm();
  const [contentType, setContentType] = useState<string>('typed');
  const [saving, setSaving]           = useState(false);
  const [uploading, setUploading]     = useState(false);
  const [uploadedFile, setUploadedFile] = useState<{ name: string; previewUrl?: string } | null>(null);

  useEffect(() => {
    loadExams();
    loadMaterials();
  }, []);

  useEffect(() => { loadMaterials(); }, [activeSection, filterExam]);

  const loadExams = async () => {
    try {
      const r = await govtApi.get('/govt/exams');
      setExams(r.data?.data ?? r.data ?? []);
    } catch { /* ignore */ }
  };

  const loadMaterials = async () => {
    setLoading(true);
    try {
      const params: any = { section: activeSection };
      if (filterExam) params.exam_id = filterExam;
      const r = await govtApi.get('/govt/admin/study', { params });
      setMaterials(r.data?.data ?? []);
    } catch {
      message.error('Failed to load materials');
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({
      section: activeSection,
      content_type: 'typed',
      is_active: true,
      is_free: true,
      thumbnail_color: '#1D4ED8',
      sort_order: 0,
    });
    setContentType('typed');
    setUploadedFile(null);
    setModal(true);
  };

  const openEdit = (m: any) => {
    setEditing(m);
    form.setFieldsValue({
      section: m.section,
      topic: m.topic,
      title: m.title,
      description: m.description,
      content_type: m.content_type,
      content_url: m.content_url,
      content_text: m.content_text,
      exam_id: m.exam_id ?? undefined,
      thumbnail_color: m.thumbnail_color ?? '#1D4ED8',
      icon: m.icon,
      is_free: m.is_free,
      is_active: m.is_active,
      sort_order: m.sort_order ?? 0,
    });
    setContentType(m.content_type);
    setUploadedFile(
      m.content_type === 'pdf' && isUploadedKey(m.content_url)
        ? { name: m.content_url.split('/').pop(), previewUrl: m.preview_url }
        : null
    );
    setModal(true);
  };

  const uploadPdf: UploadProps['customRequest'] = async (options) => {
    const { file, onSuccess, onError } = options;
    const formData = new FormData();
    formData.append('file', file as File);
    setUploading(true);
    try {
      const r = await govtApi.post('/govt/admin/study/upload', formData, {
        headers: { 'Content-Type': undefined },
      });
      const { key, url, filename } = r.data.data;
      form.setFieldValue('content_url', key);
      setUploadedFile({ name: filename, previewUrl: url });
      message.success('PDF uploaded');
      onSuccess?.(r.data);
    } catch (e: any) {
      message.error(e?.response?.data?.error || 'Upload failed');
      onError?.(e);
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    try {
      const vals = await form.validateFields();
      setSaving(true);
      if (editing) {
        await govtApi.put(`/govt/admin/study/${editing.id}`, vals);
        message.success('Updated');
      } else {
        await govtApi.post('/govt/admin/study', vals);
        message.success('Created');
      }
      setModal(false);
      loadMaterials();
    } catch (e: any) {
      if (e?.errorFields) return; // validation
      message.error('Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await govtApi.delete(`/govt/admin/study/${id}`);
      message.success('Deleted');
      loadMaterials();
    } catch {
      message.error('Delete failed');
    }
  };

  const getContentIcon = (type: string) => {
    if (type === 'pdf')  return <FilePdfOutlined style={{ color: '#E11D48' }} />;
    if (type === 'url')  return <GlobalOutlined  style={{ color: '#0891B2' }} />;
    return <FileTextOutlined style={{ color: '#7C3AED' }} />;
  };

  const sectionInfo = SECTIONS.find(s => s.key === activeSection) ?? SECTIONS[0];

  return (
    <div style={{ padding: 24 }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <Title level={3} style={{ margin: 0 }}>Study Materials</Title>
          <Text type="secondary">GK · Polity · Karnataka History · Reasoning · Kannada · Maths · English</Text>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate} size="large">
          Add Material
        </Button>
      </div>

      {/* Section tabs + exam filter */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
        <div style={{ display: 'flex', gap: 8 }}>
          {SECTIONS.map(s => (
            <Button
              key={s.key}
              type={activeSection === s.key ? 'primary' : 'default'}
              style={activeSection === s.key ? { backgroundColor: s.color, borderColor: s.color } : {}}
              onClick={() => setActiveSection(s.key)}
            >
              {s.icon} {s.label}
            </Button>
          ))}
        </div>
        <Select
          placeholder={<><FilterOutlined /> Filter by Exam</>}
          allowClear
          style={{ width: 220 }}
          value={filterExam || undefined}
          onChange={v => setFilterExam(v ?? '')}
          options={exams.map(e => ({ label: e.name, value: e.id }))}
        />
      </div>

      {/* Stats bar */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
        {[
          { label: 'Total', value: materials.length, color: sectionInfo.color },
          { label: 'Free',  value: materials.filter(m => m.is_free).length,    color: '#059669' },
          { label: 'Paid',  value: materials.filter(m => !m.is_free).length,   color: '#D97706' },
          { label: 'Active',value: materials.filter(m => m.is_active).length,  color: '#0891B2' },
        ].map(stat => (
          <Card key={stat.label} size="small" style={{ minWidth: 90, textAlign: 'center' }}>
            <div style={{ fontSize: 22, fontWeight: 700, color: stat.color }}>{stat.value}</div>
            <div style={{ fontSize: 12, color: '#666' }}>{stat.label}</div>
          </Card>
        ))}
      </div>

      {/* Material cards */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 80 }}><Spin size="large" /></div>
      ) : materials.length === 0 ? (
        <Empty description="No materials yet" style={{ marginTop: 60 }}>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>Add First Material</Button>
        </Empty>
      ) : (
        <Row gutter={[16, 16]}>
          {materials.map(m => (
            <Col key={m.id} xs={24} sm={12} lg={8} xl={6}>
              <Card
                style={{ borderRadius: 12, overflow: 'hidden', border: '1px solid #e5e7eb' }}
                bodyStyle={{ padding: 0 }}
                actions={[
                  <Button key="edit" type="text" icon={<EditOutlined />} onClick={() => openEdit(m)}>Edit</Button>,
                  <Popconfirm key="del" title="Delete this material?" onConfirm={() => handleDelete(m.id)} okText="Delete" okButtonProps={{ danger: true }}>
                    <Button type="text" danger icon={<DeleteOutlined />}>Delete</Button>
                  </Popconfirm>,
                ]}
              >
                {/* Color stripe */}
                <div style={{ height: 8, backgroundColor: m.thumbnail_color ?? sectionInfo.color }} />
                <div style={{ padding: '14px 16px' }}>
                  {/* Topic + type badges */}
                  <div style={{ display: 'flex', gap: 6, marginBottom: 8, flexWrap: 'wrap' }}>
                    <Tag color="geekblue" style={{ margin: 0 }}>{m.topic}</Tag>
                    <Tag style={{ margin: 0 }}>{getContentIcon(m.content_type)} {m.content_type.toUpperCase()}</Tag>
                    {m.is_free
                      ? <Tag color="green" style={{ margin: 0 }}>FREE</Tag>
                      : <Tag color="orange" style={{ margin: 0 }}>PAID</Tag>}
                    {!m.is_active && <Tag color="red" style={{ margin: 0 }}>INACTIVE</Tag>}
                  </div>
                  <div style={{ fontWeight: 600, fontSize: 14, color: '#111', marginBottom: 4, lineHeight: '1.4' }}>{m.title}</div>
                  {m.description && (
                    <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 8, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                      {m.description}
                    </div>
                  )}
                  {m.exam_name && (
                    <Text type="secondary" style={{ fontSize: 11 }}>📋 {m.exam_name}</Text>
                  )}
                </div>
              </Card>
            </Col>
          ))}
        </Row>
      )}

      {/* Create / Edit Modal */}
      <Modal
        title={editing ? 'Edit Study Material' : 'Add Study Material'}
        open={modal}
        onOk={handleSave}
        onCancel={() => setModal(false)}
        okText={saving ? 'Saving…' : 'Save'}
        okButtonProps={{ loading: saving }}
        width={640}
        destroyOnClose
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="section" label="Section" rules={[{ required: true }]}>
                <Select options={SECTIONS.map(s => ({ label: `${s.icon} ${s.label}`, value: s.key }))} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="topic" label="Topic" rules={[{ required: true, message: 'Topic is required' }]}>
                <Input placeholder="e.g. Karnataka History" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="title" label="Title" rules={[{ required: true }]}>
            <Input placeholder="Material title" />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input placeholder="Short description (optional)" />
          </Form.Item>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="content_type" label="Content Type" rules={[{ required: true }]}>
                <Select
                  options={CONTENT_TYPES.map(t => ({ label: <>{t.icon} {t.label}</>, value: t.value }))}
                  onChange={v => setContentType(v)}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="exam_id" label="Exam (optional)">
                <Select
                  allowClear
                  placeholder="All exams"
                  options={exams.map(e => ({ label: e.name, value: e.id }))}
                />
              </Form.Item>
            </Col>
          </Row>

          {contentType === 'url' && (
            <Form.Item name="content_url" label="Website URL" rules={[{ required: true, message: 'URL is required' }]}>
              <Input placeholder="https://..." />
            </Form.Item>
          )}

          {contentType === 'pdf' && (
            <>
              <Form.Item name="content_url" label="PDF" rules={[{ required: true, message: 'Please upload a PDF' }]}>
                <Input type="hidden" />
              </Form.Item>
              {uploadedFile ? (
                <Card size="small" style={{ marginBottom: 24, background: '#F6FFED', borderColor: '#B7EB8F' }}>
                  <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                    <Space>
                      <FilePdfOutlined style={{ color: '#E11D48', fontSize: 18 }} />
                      <Text strong>{uploadedFile.name}</Text>
                    </Space>
                    <Space>
                      {uploadedFile.previewUrl && (
                        <Button
                          size="small" icon={<EyeOutlined />}
                          href={uploadedFile.previewUrl} target="_blank" rel="noopener noreferrer">
                          Preview
                        </Button>
                      )}
                      <Upload showUploadList={false} accept="application/pdf" customRequest={uploadPdf} disabled={uploading}>
                        <Button size="small" icon={<ReloadOutlined />} loading={uploading}>Replace</Button>
                      </Upload>
                    </Space>
                  </Space>
                </Card>
              ) : (
                <Form.Item label=" " colon={false}>
                  <Dragger
                    showUploadList={false}
                    accept="application/pdf"
                    customRequest={uploadPdf}
                    disabled={uploading}
                    style={{ background: '#FAFAFA' }}>
                    <p className="ant-upload-drag-icon">
                      {uploading ? <FilePdfOutlined style={{ color: '#1D4ED8' }} /> : <InboxOutlined />}
                    </p>
                    <p className="ant-upload-text">{uploading ? 'Uploading…' : 'Click or drag a PDF file here'}</p>
                    <p className="ant-upload-hint">Up to 50MB</p>
                  </Dragger>
                </Form.Item>
              )}
            </>
          )}
          {(contentType === 'typed' || contentType === 'text') && (
            <Form.Item name="content_text" label="Content (supports Markdown)" rules={[{ required: true }]}>
              <TextArea rows={10} placeholder={'# Heading\n## Sub Heading\n**bold**, *italic*\n- bullet item\n| col1 | col2 |\n|---|---|\n| data | data |'} />
            </Form.Item>
          )}

          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="thumbnail_color" label="Thumbnail Color">
                <Select options={COLORS.map(c => ({
                  label: <span><span style={{ display: 'inline-block', width: 14, height: 14, borderRadius: 3, backgroundColor: c.value, marginRight: 8, verticalAlign: 'middle' }} />{c.label}</span>,
                  value: c.value,
                }))} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="sort_order" label="Sort Order">
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col span={8}>
              <Form.Item name="is_free" label="Free Access" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="is_active" label="Active" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};

export default StudyMaterialsPage;
